import type { AppLang } from "@/locale/appLang";
import { uk } from "./offerSite.uk";
import { kk } from "./offerSite.kk";
import { uz } from "./offerSite.uz";
import { ky } from "./offerSite.ky";

export type OfferCard = {
  id: "incident" | "audit" | "retainer" | "projects";
  href: string;
  title: string;
  price: string;
  blurb: string;
  bullets: string[];
};

export type ProofItem = { brand: string; note: string };

export type CaseItem = {
  title: string;
  problem: string;
  did: string;
  result: string;
};

export type ServicePageContent = {
  id: OfferCard["id"];
  path: string;
  seo: { title: string; description: string };
  breadcrumb: string;
  h1: string;
  lead: string;
  price: string;
  forWhom: string[];
  includes: string[];
  cta: string;
};

export type OfferSiteBundle = {
  seoHome: { title: string; description: string };
  ui: {
    open: string;
    forWhom: string;
    includes: string;
  };
  nav: {
    services: string;
    cases: string;
    about: string;
    contacts: string;
    telegramCta: string;
  };
  hero: {
    eyebrow: string;
    h1: string;
    lead: string;
    ctaTg: string;
    ctaOffers: string;
    chips: string[];
  };
  proofTitle: string;
  proofLead: string;
  proof: ProofItem[];
  offersTitle: string;
  offersLead: string;
  offers: OfferCard[];
  payTitle: string;
  payItems: string[];
  payNote: string;
  howTitle: string;
  howSteps: { t: string; p: string }[];
  aboutTeaser: {
    title: string;
    text: string;
    link: string;
  };
  footerTagline: string;
  footerEuNote: string;
  footerEuLink: string;
  casesPage: {
    seo: { title: string; description: string };
    h1: string;
    lead: string;
    labels: { problem: string; did: string; result: string };
    items: CaseItem[];
  };
  aboutPage: {
    seo: { title: string; description: string };
    h1: string;
    lead: string;
    body: string[];
    stackTitle: string;
    stack: string[];
  };
  contactsPage: {
    seo: { title: string; description: string };
    h1: string;
    lead: string;
    tgHint: string;
    payTitle: string;
    payItems: string[];
    payNote: string;
  };
  services: Record<OfferCard["id"], ServicePageContent>;
};

const ruOffers: OfferCard[] = [
  {
    id: "incident",
    href: "/services/incident",
    title: "Неотложная помощь",
    price: "$100–150/ч · пакет от $600",
    blurb: "Восстановление production при сбое, DDoS или компрометации доступа.",
    bullets: ["Реакция в Telegram", "Разбор после инцидента", "Без абонентского договора"],
  },
  {
    id: "audit",
    href: "/services/audit",
    title: "Аудит 1–2 дня",
    price: "$300–600",
    blurb: "Оценка рисков инфраструктуры, готовности к PCI DSS и устойчивости к атакам.",
    bullets: ["Письменный отчёт", "Приоритеты устранения", "Созвон 30–60 мин"],
  },
  {
    id: "retainer",
    href: "/services/retainer",
    title: "Retainer — сопровождение инфраструктуры",
    price: "$1 500–2 500/мес",
    blurb: "Внешний DevOps для криптовалютных и финтех-проектов: релизы, мониторинг, инциденты.",
    bullets: ["40–80 ч эквивалента", "Telegram-канал", "Резервное копирование с проверкой восстановления"],
  },
  {
    id: "projects",
    href: "/services/projects",
    title: "Проект под ключ",
    price: "$3 000–8 000 fixed",
    blurb: "PCI DSS-контур, защита от DDoS, миграция, Kubernetes и CI/CD, ноды и хранилища секретов.",
    bullets: ["2–4 недели", "Фиксированная цена", "Документация передачи"],
  },
];

export const ru: OfferSiteBundle = {
  seoHome: {
    title: "Инфраструктура для криптовалютного сектора, финтеха и highload | IT-24.pro",
    description:
      "Инженер инфраструктуры: DDoS, PCI DSS, Kubernetes, ноды, vaults. Опыт банков и бирж. Telegram, USDT/UAH/инвойс. Retainer от $1 500/мес.",
  },
  ui: {
    open: "Открыть",
    forWhom: "Для кого",
    includes: "Что входит",
  },
  nav: {
    services: "Услуги",
    cases: "Кейсы",
    about: "Обо мне",
    contacts: "Контакты",
    telegramCta: "Написать в Telegram",
  },
  hero: {
    eyebrow: "IT-24.pro · криптовалютный сектор · платежи · highload",
    h1: "Инфраструктура, где простой стоит денег",
    lead:
      "Делаю и сопровождаю production для бирж, платежей и высоконагруженных сервисов: DDoS, PCI DSS, Kubernetes, секреты и ноды. Работаю сам — отвечаю в Telegram.",
    ctaTg: "Написать в Telegram",
    ctaOffers: "Смотреть услуги и цены",
    chips: ["PCI DSS L1/L2", "DDoS 100+ Gbps", "USDT / инвойс PL", "С 2010 в инфраструктуре"],
  },
  proofTitle: "Опыт по именам",
  proofLead: "То, что можно сверить с резюме и кейсами — без «500+ проектов».",
  proof: [
    { brand: "Raiffeisen Bank", note: "AWS, PCI, audit CI/CD" },
    { brand: "Crypto exchange", note: "EKS, WAF, ноды, vaults" },
    { brand: "Epicenter / 27.ua", note: "Highload, Ceph, кластер" },
    { brand: "DDoS", note: "100 Gbps / кейс ~10M pps" },
  ],
  offersTitle: "Услуги",
  offersLead: "Четыре формата. Без VPS и тарифов «администратор за $80».",
  offers: ruOffers,
  payTitle: "Как платят",
  payItems: ["USDT (TRC-20)", "UAH (карта / ФОП)", "Wise / Payoneer", "Польский инвойс с VAT"],
  payNote:
    "Юридическое лицо в Польше — удобно для украинского бизнеса с клиентами в ЕС и для расчётов в USDT.",
  howTitle: "Как начинаем",
  howSteps: [
    { t: "Сообщение в Telegram", p: "Коротко: что горит, стек, бюджет." },
    { t: "Созвон 30 минут", p: "Разберём: инцидент, аудит, retainer или разовый проект." },
    { t: "Аудит или пилот", p: "Небольшой вход — без годового контракта «вслепую»." },
    { t: "Retainer или fixed", p: "Дальше — сопровождение или проект с понятным результатом." },
  ],
  aboutTeaser: {
    title: "Игорь Косицын",
    text: "DevOps / MLOps, Konin (PL). Linux highload с 2010-х, cloud-native последние годы. Юрлицо в Польше.",
    link: "Подробнее",
  },
  footerTagline:
    "Инфраструктура для криптовалютного сектора, финтеха и высоконагруженных систем. Русскоязычный рынок · юрлицо PL · Telegram.",
  footerEuNote: "Для рынка EU — отдельный сайт",
  footerEuLink: "nexops.eu",
  casesPage: {
    seo: {
      title: "Кейсы: банк, биржи, DDoS, highload | IT-24.pro",
      description:
        "Примеры проектов: PCI DSS, инфраструктура криптовалютных бирж, защита от атак, e-commerce highload.",
    },
    h1: "Кейсы",
    lead: "Задача, что сделал, чем закончилось.",
    labels: { problem: "Задача", did: "Решение", result: "Результат" },
    items: [
      {
        title: "Криптовалютная биржа: production и комплаенс",
        problem:
          "Нужна была инфраструктура обмена и узлов без ручных релизов и с нормальным контролем секретов.",
        did: "AWS EKS и bare-metal Kubernetes, Helm, GitLab CI, WAF, Ceph, ноды, vaults, Ansible и Terraform.",
        result: "Релизы без ручной рутины, контур под аудит, сопровождение в production.",
      },
      {
        title: "Raiffeisen: миграция и PCI DSS",
        problem: "Перенос инфраструктуры и требования финансового сектора к аудиту и доступам.",
        did: "Миграция в AWS, Kubernetes, hardening CI/CD, участие в подготовке к PCI DSS Level 1/2 со стороны инфраструктуры.",
        result: "Готовность к проверкам и воспроизводимые деплои.",
      },
      {
        title: "DDoS при рекламном запуске",
        problem: "Пик до ~10 млн пакетов в секунду; дата-центры не отфильтровывали атаку.",
        did: "Фильтрация L3–L7, перенос критичных сервисов на защищённый контур, мониторинг, резервные маршруты.",
        result: "Около 80% аудитории сохраняли доступ за месяц атаки.",
      },
      {
        title: "Электронная коммерция / ритейл, highload",
        problem: "Пики нагрузки, хранилище на сотни терабайт, доступ из разных регионов.",
        did: "Nginx, MySQL, Ceph, кластеры, кэш, схема Украина — Франция.",
        result: "Стабильная работа под нагрузкой и защищённое хранение данных.",
      },
    ],
  },
  aboutPage: {
    seo: {
      title: "Обо мне — Игорь Косицын, DevOps/MLOps | IT-24.pro",
      description:
        "Инженер инфраструктуры для криптовалютного сектора и финтеха. Польша, банки и биржи, PCI DSS, DDoS, Kubernetes.",
    },
    h1: "Обо мне",
    lead: "Игорь Косицын. DevOps / MLOps: проектирую и сопровождаю инфраструктуру сам, без «команды из двадцати» на сайте.",
    body: [
      "Юрлицо в Польше (Konin). Раньше строил и вёл инфраструктуру в Украине — банки, ритейл, криптовалютные биржи.",
      "Беру задачи, где простой или взлом бьют по деньгам и проверкам: платежи, обмен, пиковые нагрузки, подготовка к PCI DSS.",
      "Клиентов из ЕС веду на nexops.eu. IT-24.pro — для русскоязычных заказчиков из СНГ.",
    ],
    stackTitle: "Стек",
    stack: [
      "Kubernetes, Helm, Rancher, Docker",
      "AWS (EKS, RDS, S3, Shield), Hetzner bare-metal",
      "Terraform, Ansible, GitLab CI",
      "Prometheus, Grafana, Loki, ELK",
      "Cloudflare WAF/DDoS, Vault, PCI DSS",
      "PostgreSQL, MongoDB, Ceph, RabbitMQ",
      "Ollama / vLLM, RAG (ChromaDB) — по запросу",
    ],
  },
  contactsPage: {
    seo: {
      title: "Контакты — Telegram, оплата USDT | IT-24.pro",
      description: "Напишите в Telegram @devops_it24. USDT, UAH, польский инвойс.",
    },
    h1: "Контакты",
    lead: "Основной канал — Telegram. Отвечаю сам, без тикетной очереди.",
    tgHint: "Кратко: стек и что сейчас болит.",
    payTitle: "Оплата",
    payItems: ["USDT TRC-20", "UAH", "Wise / Payoneer", "Инвойс PL (VAT)"],
    payNote:
      "Юрлицо в Польше — удобно для украинского бизнеса с клиентами в ЕС и для расчётов в USDT.",
  },
  services: {
    incident: {
      id: "incident",
      path: "/services/incident",
      seo: {
        title: "Неотложная помощь по инфраструктуре — инциденты и DDoS | IT-24.pro",
        description:
          "Восстановление production при атаке, компрометации или падении сервиса. $100–150/ч или пакет от $600. Telegram.",
      },
      breadcrumb: "Неотложная помощь",
      h1: "Неотложная помощь",
      lead: "Если production лежит — сначала поднимаю доступность, потом разбираю причину. Без выбора «пакета Growth» посреди инцидента.",
      price: "$100–150/час · пакет инцидента от $600",
      forWhom: [
        "Криптовалютные, платёжные и сервисы с рекламным бюджетом",
        "Нет своего DevOps on-call",
        "Атака, компрометация, потеря доступа, угроза данным",
      ],
      includes: [
        "Диагностика и восстановление доступности",
        "Первичный hardening (доступ, firewall, очевидные дыры)",
        "Короткий post-mortem: что случилось и что чинить дальше",
        "По договорённости — переход к аудиту или retainer",
      ],
      cta: "Написать в Telegram об инциденте",
    },
    audit: {
      id: "audit",
      path: "/services/audit",
      seo: {
        title: "Аудит инфраструктуры и готовности к PCI DSS | IT-24.pro",
        description: "Аудит 1–2 дня: безопасность, контур DDoS, облачный счёт, PCI DSS. $300–600.",
      },
      breadcrumb: "Аудит",
      h1: "Аудит 1–2 дня",
      lead: "Платите за ясность по рискам — без годового контракта «вслепую».",
      price: "$300–600 fixed",
      forWhom: [
        "Подготовка к PCI DSS или внешнему аудиту",
        "Растёт счёт у облачного провайдера",
        "Непонятна устойчивость к атаке или пику",
      ],
      includes: [
        "Обзор архитектуры, доступов, бэкапов, мониторинга",
        "Список рисков по приоритету",
        "Оценка бюджета на устранение (retainer или проект)",
        "Созвон по отчёту",
      ],
      cta: "Заказать аудит в Telegram",
    },
    retainer: {
      id: "retainer",
      path: "/services/retainer",
      seo: {
        title: "DevOps retainer для криптовалютного сектора и финтеха | IT-24.pro",
        description:
          "Абонентское сопровождение $1 500–2 500/мес: релизы, мониторинг, инциденты, резервное копирование.",
      },
      breadcrumb: "Retainer",
      h1: "Retainer — сопровождение инфраструктуры",
      lead: "Сравнивайте не с хостингом за $100, а со Middle/Senior DevOps за $2.7–5k/мес с отпусками и bus factor.",
      price: "$1 500–2 500 / месяц",
      forWhom: [
        "Криптовалютный, платёжный, финтех или highload-продукт",
        "Студия без своего DevOps на клиентских проектах",
        "Нужен ответственный контакт в Telegram, а не тикетная служба",
      ],
      includes: [
        "Мониторинг и реакция на инциденты в оговорённом окне",
        "Релизы, CI/CD и правки инфраструктуры в лимите часов",
        "Обновления, бэкапы, периодическая проверка восстановления",
        "Короткий отчёт раз в месяц: сделанное и зоны риска",
      ],
      cta: "Обсудить retainer в Telegram",
    },
    projects: {
      id: "projects",
      path: "/services/projects",
      seo: {
        title: "PCI DSS, DDoS, Kubernetes — проекты под ключ | IT-24.pro",
        description:
          "Фиксированная цена $3–8k: контур PCI DSS, защита от DDoS, миграция, Kubernetes и CI/CD, ноды и vaults.",
      },
      breadcrumb: "Проекты",
      h1: "Проект под ключ",
      lead: "Когда нужен понятный результат за 2–4 недели, а не оплата «часов в месяц».",
      price: "$3 000–8 000 fixed · 2–4 недели",
      forWhom: [
        "Подготовка инфраструктуры к PCI DSS",
        "Контур DDoS / WAF после инцидента или перед запуском",
        "Миграция (облако ↔ bare-metal), Kubernetes с нуля, ноды и vaults",
      ],
      includes: [
        "Скоп и фиксированная цена до старта",
        "IaC, документация, передача доступов",
        "Критерии приёмки (доступность, пайплайн, восстановление из бэкапа)",
        "Опция: сопровождение после запуска (retainer)",
      ],
      cta: "Описать проект в Telegram",
    },
  },
};

const BUNDLES: Record<AppLang, OfferSiteBundle> = { ru, uk, kk, uz, ky };

export function pickOfferSite(lang: AppLang): OfferSiteBundle {
  return BUNDLES[lang] ?? ru;
}
